/**
 * ConversationDetailPanel — the new right-side conversation view.
 *
 * Opens as a wide slide-in drawer over the inbox and mirrors the KC2
 * "interaction view" layout: a header action bar, a left conversation
 * thread/tabs column, and a right properties + SLA column. Reuses the existing
 * data layer (useConversation, socket, PATCH endpoints) but is presented as a
 * fresh, ≤3px-radius UI. Action flows open as nested drawers.
 */
import { useCallback, useEffect, useMemo, useRef, useState, type JSX } from 'react'
import { api } from '../../../api/client.js'
import { useConversation } from '../../../hooks/useConversation.js'
import { useConversationsStore } from '../../../store/conversationsStore.js'
import { getSocket } from '../../../socket/client.js'
import type { MessageApproval, MessageStatus } from '../../../modules/conversations/types.js'
import DetailHeader from './DetailHeader.js'
import ConversationTabs, { type TabKey } from './ConversationTabs.js'
import PropertiesPanel from './PropertiesPanel.js'
import SlaOlaPanel from './SlaOlaPanel.js'
import TransferDrawer from './TransferDrawer.js'
import CloseDrawer from './CloseDrawer.js'
import CollaborateDrawer from './CollaborateDrawer.js'
import ReplyDrawer from './ReplyDrawer.js'
import ApprovalsDrawer from './ApprovalsDrawer.js'
import SendForApprovalDrawer from './SendForApprovalDrawer.js'
import ReviewDrawer from './ReviewDrawer.js'
import SelfAssignDrawer from './SelfAssignDrawer.js'
import ChildTicketDrawer from './ChildTicketDrawer.js'
import CreateConversationPanel from '../CreateConversationPanel.js'
import ConversationSearchDrawer from '../ConversationSearchDrawer.js'
import MergeDrawer, { type MergeCandidate } from './MergeDrawer.js'
import { ChevronsLeft, ChevronsRight, GitMerge, Link2, Network, UserPlus, type LucideIcon } from 'lucide-react'
import { useConversationActions } from './useConversationActions.js'
import { useToast } from '../../../components/system/ToastProvider.js'
import DetailStrip from './DetailStrip.js'
import AttachmentsSection from './AttachmentsSection.js'
import { DescriptionTranscript } from './DescriptionTranscript.js'
import { FacebookPostBanner } from '../../facebook/FacebookPostBanner.js'
import { WhatsAppChatBanner } from '../../whatsapp/WhatsAppChatBanner.js'
import { InstagramPostBanner } from '../../instagram/InstagramPostBanner.js'
import { InstagramChatBanner } from '../../instagram/InstagramChatBanner.js'
import { formatCustomerName, cleanIdentifier } from '../../../utils/formatCustomerName.js'
import AttachmentViewer from './AttachmentViewer.js'
import { R, htmlToText } from './primitives.js'

type DrawerKey = 'transfer' | 'close' | 'collaborate' | 'self-assign' | null

/** localStorage key for the right-rail open/collapsed preference. */
const PROPERTIES_RAIL_PREF_KEY = 'kc3.conversationDetail.propertiesRailOpen'

export interface ConversationDetailPanelProps {
  conversationId: string | null
  open: boolean
  onClose: () => void
  /** Called after any mutation so the parent inbox can refresh counts/list. */
  onChanged?: () => void
  /** 'drawer' (default) renders the usual backdrop + slide-in-from-right
   *  overlay. 'page' renders the exact same content in-flow (no fixed
   *  positioning/backdrop) so it can be mounted as a full page — e.g. an
   *  in-app tab — instead of an overlay on top of the list it was opened
   *  from. onClose is still called by the header's close action in 'page'
   *  mode; the caller decides what that means (e.g. navigate back). */
  mode?: 'drawer' | 'page'
}

export default function ConversationDetailPanel({
  conversationId: conversationIdProp,
  open,
  onClose,
  onChanged,
  mode = 'drawer',
}: ConversationDetailPanelProps): JSX.Element | null {
  // Navigating to a related conversation (parent / child / linked) swaps the
  // drawer in-place by overriding the inbox-provided id. Cleared whenever the
  // inbox opens a different conversation.
  const [navId, setNavId] = useState<string | null>(null)
  const conversationId = navId ?? conversationIdProp
  useEffect(() => { setNavId(null) }, [conversationIdProp])

  const {
    conversation,
    messages,
    conversationAttachments,
    activityLog,
    loading,
    error,
    appendMessage,
    replaceMessage,
    patchMessage,
    markMessageFailed,
    refetch,
  } = useConversation(open ? conversationId : null)

  const [currentAgentId, setCurrentAgentId] = useState<string | null>(null)
  const [canReview, setCanReview] = useState(false)
  const [tab, setTab] = useState<TabKey>('timeline')
  const [drawer, setDrawer] = useState<DrawerKey>(null)
  const [replyOpen, setReplyOpen] = useState(false)
  const [replyMode, setReplyMode] = useState<'reply' | 'note'>('reply')
  const [approvalsOpen, setApprovalsOpen] = useState(false)
  const [sendApprovalOpen, setSendApprovalOpen] = useState(false)
  const [reviewOpen, setReviewOpen] = useState(false)
  const [viewerOpen, setViewerOpen] = useState(false)
  const [viewerIndex, setViewerIndex] = useState(0)
  // Add-Child flow: the chooser (create-new vs link-existing), and the reused
  // New Conversation form opened in child mode.
  const [childChooserOpen, setChildChooserOpen] = useState(false)
  const [createChildOpen, setCreateChildOpen] = useState(false)
  const [linkExistingOpen, setLinkExistingOpen] = useState(false)
  // "Link" flow (KC2 parity): multi-select existing conversations to link.
  const [linkOpen, setLinkOpen] = useState(false)
  const [linkedRefreshKey, setLinkedRefreshKey] = useState(0)
  const [transfersRefreshKey, setTransfersRefreshKey] = useState(0)
  // "Merge" flow: pick the conversations in the Client Info tab, then MergeDrawer
  // decides which one survives as the primary and how the merge behaves.
  const [mergeCandidates, setMergeCandidates] = useState<MergeCandidate[] | null>(null)
  // Right rail (properties + SLA) collapse — persisted like the inbox column
  // preferences so the agent's choice survives between conversations.
  const [propertiesOpen, setPropertiesOpen] = useState<boolean>(() => {
    try { return localStorage.getItem(PROPERTIES_RAIL_PREF_KEY) !== '0' } catch { return true }
  })
  const togglePropertiesRail = (): void => {
    setPropertiesOpen((v) => {
      const next = !v
      try { localStorage.setItem(PROPERTIES_RAIL_PREF_KEY, next ? '1' : '0') } catch { /* preference only */ }
      return next
    })
  }
  const toast = useToast()

  // Aggregated attachments list (message + conversation attachments, deduped)
  // — shared between AttachmentsSection and AttachmentViewer so indices match.
  const allAttachments = useMemo(() => {
    const seen = new Set<string>()
    const all: (typeof messages[number]['attachments'][number] & { url?: string })[] = []
    for (const m of messages) {
      for (const a of m.attachments) {
        if (!seen.has(a.id)) { seen.add(a.id); all.push(a) }
      }
    }
    for (const a of conversationAttachments) {
      if (!seen.has(a.id)) { seen.add(a.id); all.push(a) }
    }
    return all
  }, [messages, conversationAttachments])

  // Extra attachments from notes (loaded on-demand when clicking note attachments)
  const [extraAttachments, setExtraAttachments] = useState<typeof allAttachments>([])
  const viewerAttachments = useMemo(() => [...allAttachments, ...extraAttachments], [allAttachments, extraAttachments])

  // Keep onChanged in a ref so the socket effect doesn't churn on every render.
  const onChangedRef = useRef(onChanged)
  onChangedRef.current = onChanged

  const refetchAll = useCallback(() => {
    void refetch()
    onChangedRef.current?.()
  }, [refetch])

  // A new message moves the conversation's Last Modified At / By, but those
  // come from the activity log, which only the detail fetch carries — the
  // socket event alone just appends to the thread. Refresh behind it so the
  // strip tracks the reply that was in fact just sent. Debounced: a burst of
  // messages should cost one fetch, not one each.
  const activityRefreshRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const scheduleDetailRefresh = useCallback(() => {
    if (activityRefreshRef.current) clearTimeout(activityRefreshRef.current)
    activityRefreshRef.current = setTimeout(() => {
      activityRefreshRef.current = null
      void refetch()
    }, 400)
  }, [refetch])
  useEffect(
    () => () => {
      if (activityRefreshRef.current) clearTimeout(activityRefreshRef.current)
    },
    [],
  )

  const actions = useConversationActions(conversationId, refetchAll)

  const ratingScale = useConversationsStore((s) => s.ratingScale)

  // Pending message approvals for this conversation (from the store).
  const approvalsMap = useConversationsStore((s) => s.messageApprovalsByMessageId)
  const pendingApprovals = useMemo<MessageApproval[]>(
    () =>
      Object.values(approvalsMap).filter(
        (a) => a.conversationId === conversationId && a.status === 'pending',
      ),
    [approvalsMap, conversationId],
  )

  // Only the assignee may edit, reply, change status, or perform any action
  // on the conversation. Everyone else sees read-only buttons. A collaborator
  // (co-agent) must self-assign to take ownership before they can act.
  const isAssignee =
    currentAgentId !== null &&
    conversation !== null &&
    currentAgentId === conversation.assignedAgentId
  // A merged secondary keeps showing its own conversation, but it is a closed
  // record of what happened on that ticket — replies belong on the primary it
  // was merged into, so nobody can act on it regardless of assignment.
  const isMergedSecondary = conversation?.mergedIntoId != null
  const canEdit = isAssignee && !isMergedSecondary
  const canReply = isAssignee && !isMergedSecondary
  const hasCollaborators = (conversation?.coAgentIds ?? []).length > 0

  // Which Facebook post this conversation is about. The conversation metadata
  // holds it for tickets raised by the comment poller, but tickets created
  // before that metadata existed still carry it on the comment message, so
  // fall back to the first message that has any post context.
  const facebookPost = useMemo(() => {
    if ((conversation?.channelOrigin ?? '').toLowerCase() !== 'facebook') {
      return { postId: null, postMessage: null, postPicture: null, permalink: null }
    }
    const metaMessage = conversation?.fbPostMessage ?? null
    const metaPicture = conversation?.fbPostPicture ?? null
    const metaLink = conversation?.fbPermalink ?? null
    const metaPostId = conversation?.fbPostId ?? null

    const fromMessage = messages.find(
      (m) => m.fbPostId || m.fbPostMessage || m.fbPostPicture || m.fbPermalink,
    )

    const postId = metaPostId ?? fromMessage?.fbPostId ?? null
    const postMessage = metaMessage ?? fromMessage?.fbPostMessage ?? null
    const postPicture = metaPicture ?? fromMessage?.fbPostPicture ?? null
    const permalink = metaLink ?? fromMessage?.fbPermalink ?? null

    // Nothing at all → no banner. A post id on its own is enough to know a
    // post is involved, but with no caption, picture or link there is nothing
    // to show.
    if (!postMessage && !postPicture && !permalink) {
      return { postId: null, postMessage: null, postPicture: null, permalink: null }
    }
    return { postId, postMessage, postPicture, permalink }
  }, [conversation?.channelOrigin, conversation?.fbPostId, conversation?.fbPostMessage, conversation?.fbPostPicture, conversation?.fbPermalink, messages])

  // Which Instagram post this conversation is about (for Instagram post comments).
  const instagramPost = useMemo(() => {
    if ((conversation?.channelOrigin ?? '').toLowerCase() !== 'instagram') {
      return { postId: null, postMessage: null, postPicture: null, permalink: null }
    }
    const metaMessage = conversation?.igPostMessage ?? null
    const metaPicture = conversation?.igPostPicture ?? null
    const metaLink = conversation?.igPermalink ?? null
    const metaPostId = conversation?.igPostId ?? null

    const fromMessage = messages.find(
      (m) => m.igPostId || m.igPostMessage || m.igPostPicture || m.igPermalink,
    )

    const postId = metaPostId ?? fromMessage?.igPostId ?? null
    const postMessage = metaMessage ?? fromMessage?.igPostMessage ?? null
    const postPicture = metaPicture ?? fromMessage?.igPostPicture ?? null
    const permalink = metaLink ?? fromMessage?.igPermalink ?? null

    if (!postId && !postMessage && !postPicture && !permalink) {
      return { postId: null, postMessage: null, postPicture: null, permalink: null }
    }
    return { postId, postMessage, postPicture, permalink }
  }, [conversation?.channelOrigin, conversation?.igPostId, conversation?.igPostMessage, conversation?.igPostPicture, conversation?.igPermalink, messages])

  // Rail action gating (mirrors KC2): each requires the conversation to be
  // assigned to you and open; Add-Child/Link additionally exclude conversations that
  // are already nested (a child can't have children; parent/child can't link).
  const isClosedConv = conversation?.status === 'closed'
  const isChildConv = (conversation?.parentId ?? null) !== null
  const isParentConv = conversation?.isParent === true
  const addChildDisabled = !isAssignee || isClosedConv || isChildConv
  const linkDisabled = !isAssignee || isClosedConv || isParentConv || isChildConv
  const collaborateDisabled = !isAssignee || isClosedConv
  const railReason = (kind: 'child' | 'link' | 'collab'): string | undefined => {
    if (!isAssignee) return 'Assign the conversation to yourself first'
    if (isClosedConv) return 'Conversation is closed'
    if (kind === 'child' && isChildConv) return "A child conversation can't have its own children"
    if (kind === 'link' && (isParentConv || isChildConv)) return "Parent/child conversations can't be linked"
    return undefined
  }

  const selfAssign = (): void => {
    setDrawer('self-assign')
  }

  // Transfer requires every required additional property *of this conversation's
  // form* to be filled first (mirrors the reference's "all mandatory props set").
  // Scoping to the form matters: a required property that isn't part of this
  // conversation's form can never be filled here, so it must not block transfer.
  const conversationFormId = conversation?.conversationFormId ?? null
  const [, setFormSelectedProps] = useState<string[] | null>(null)
  useEffect(() => {
    if (!conversationFormId) {
      setFormSelectedProps(null)
      return
    }
    let cancelled = false
    api
      .get<{ form: { selectedProperties?: string[] } }>(
        `/admin/conversation-management/forms/${conversationFormId}`,
      )
      .then((r) => {
        if (!cancelled) setFormSelectedProps(r.form?.selectedProperties ?? [])
      })
      .catch(() => {
        if (!cancelled) setFormSelectedProps(null)
      })
    return () => {
      cancelled = true
    }
  }, [conversationFormId])

  const requiredPropsFilled = useMemo(() => {
    if (!conversation) return true
    // Transfer requires all classification properties to be set
    // (mirrors KC2: state, type, category, subcategory, impact, priority, complexity, urgency).
    const required = [
      conversation.statusId,
      conversation.typeId,
      conversation.categoryId,
      conversation.subcategoryId,
      conversation.impactId,
      conversation.priorityId,
      conversation.complexityId,
      conversation.urgencyId,
    ]
    return required.every((v) => v !== null && v !== undefined && String(v).trim() !== '')
  }, [conversation])
  // Reset transient UI whenever a different conversation is opened.
  useEffect(() => {
    setTab('timeline')
    setDrawer(null)
    setReplyOpen(false)
    setApprovalsOpen(false)
    setSendApprovalOpen(false)
    setReviewOpen(false)
    setViewerOpen(false)
    setExtraAttachments([])
    setChildChooserOpen(false)
    setCreateChildOpen(false)
    setLinkExistingOpen(false)
    setLinkOpen(false)
  }, [conversationId])

  // Resolve the current agent (agents.id — used for own-message styling).
  // Also fetch conversation-level approval requests to determine if the
  // current user is the approver of any pending request (enables Review button).
  useEffect(() => {
    if (!open) return
    let cancelled = false
    Promise.all([
      api.get<{ id: string }>('/agent/me'),
      api.get<{ requests: { approverId: string | null; reviewed: boolean }[] }>(
        `/conversations/${conversationId}/approval-requests`,
      ),
    ])
      .then(([agentRes, reqRes]) => {
        if (cancelled) return
        setCurrentAgentId(agentRes.id)
        const pending = (reqRes.requests ?? []).filter((r) => !r.reviewed)
        setCanReview(pending.some((r) => r.approverId === agentRes.id))
      })
      .catch(() => {
        if (!cancelled) {
          setCurrentAgentId(null)
          setCanReview(false)
        }
      })
    return () => {
      cancelled = true
    }
  }, [open, conversationId])

  // Join the per-conversation socket room for live updates.
  useEffect(() => {
    if (!open || !conversationId) return
    const socket = getSocket()
    socket.emit('conversation:open', { conversationId })

    const onMessageNew = (data: unknown): void => {
      const e = data as {
        conversationId?: string
        messageId?: string
        direction?: 'inbound' | 'outbound'
        body?: string
        createdAt?: string
      }
      if (e.conversationId !== conversationId || !e.messageId) return
      scheduleDetailRefresh()
      const existing = useConversationsStore.getState().messagesByConversationId[conversationId] ?? []
      if (existing.some((m) => m.id === e.messageId)) return
      if (e.direction === 'outbound') {
        const pendingOwn = existing.find(
          (m) =>
            m.direction === 'outbound' &&
            (m.status === 'sending' || m.status === 'queued') &&
            m.body.trim() === (e.body ?? '').trim(),
        )
        if (pendingOwn) {
          replaceMessage(pendingOwn.id, {
            ...pendingOwn,
            id: e.messageId,
            status: 'queued',
            createdAt: e.createdAt ?? pendingOwn.createdAt,
          })
          return
        }
      }
      appendMessage({
        id: e.messageId,
        conversationId,
        direction: e.direction ?? 'inbound',
        senderHandle: '',
        senderAgentId: null,
        senderAgentName: null,
        senderName: null,
        body: e.body ?? '',
        visibility: 'customer',
        attachments: [],
        status: e.direction === 'outbound' ? 'queued' : 'delivered',
        createdAt: e.createdAt ?? new Date().toISOString(),
      })
    }

    const onMessageStatusChanged = (data: unknown): void => {
      const e = data as {
        conversationId?: string
        messageId?: string
        status?: string
      }
      if (e.conversationId !== conversationId || !e.messageId || !e.status) return
      patchMessage(e.messageId, { status: e.status as MessageStatus })
    }

    const onAny = (): void => {
      void refetch()
      onChangedRef.current?.()
    }

    socket.on('conversation:message:new', onMessageNew)
    socket.on('conversation:message:status:changed', onMessageStatusChanged)
    socket.on('conversation:status:changed', onAny)
    socket.on('conversation:priority:changed', onAny)
    socket.on('conversation:classification:changed', onAny)
    socket.on('conversation:assigned', onAny)
    const onTransferred = (): void => {
      setTransfersRefreshKey((k) => k + 1)
      onAny()
    }
    socket.on('conversation:transferred', onTransferred)
    socket.on('conversation:closed', onAny)
    socket.on('conversation:resolved', onAny)
    socket.on('conversation:csat:updated', onAny)
    socket.on('conversation:co_agent:added', onAny)
    socket.on('conversation:co_agent:removed', onAny)
    socket.on('conversation:message_approval:requested', onAny)
    socket.on('conversation:message_approval:decided', onAny)

    return () => {
      socket.emit('conversation:close', { conversationId })
      socket.off('conversation:message:new', onMessageNew)
      socket.off('conversation:message:status:changed', onMessageStatusChanged)
      socket.off('conversation:status:changed', onAny)
      socket.off('conversation:priority:changed', onAny)
      socket.off('conversation:classification:changed', onAny)
      socket.off('conversation:assigned', onAny)
      socket.off('conversation:transferred', onTransferred)
      socket.off('conversation:closed', onAny)
      socket.off('conversation:resolved', onAny)
      socket.off('conversation:csat:updated', onAny)
      socket.off('conversation:co_agent:added', onAny)
      socket.off('conversation:co_agent:removed', onAny)
      socket.off('conversation:message_approval:requested', onAny)
      socket.off('conversation:message_approval:decided', onAny)
    }
    // appendMessage/refetch are stable (useCallback); intentionally re-subscribe
    // only when the open conversation changes.
  }, [open, conversationId])

  if (!open || conversationId === null) return null

  const wrapperClassName =
    mode === 'page'
      ? 'scrollbar-thin-tree flex h-full w-full flex-col bg-[#F8FAFC]'
      : 'scrollbar-thin-tree animate-slide-in-right fixed right-0 top-0 z-50 flex h-full w-[90%] flex-col bg-[#F8FAFC] shadow-2xl'

  return (
    <>
      {/* Backdrop — drawer mode only; page mode is in-flow content, nothing to dim. */}
      {mode === 'drawer' && (
        <div className="fixed inset-0 z-40 bg-black/20 backdrop-blur-sm" onClick={onClose} />
      )}

      <div className={wrapperClassName}>
        {loading && !conversation ? (
          <PanelSkeleton onClose={onClose} />
        ) : error || !conversation ? (
          <PanelError message={error ?? 'Conversation not found.'} onRetry={() => void refetch()} onClose={onClose} />
        ) : (
          <>
            <DetailHeader
              conversation={conversation}
              canSelfAssign={currentAgentId !== null && currentAgentId !== conversation.assignedAgentId}
              isAssignedToMe={isAssignee}
              canReply={canReply}
              hasCollaborators={hasCollaborators}
              requiredPropsFilled={requiredPropsFilled}
              canReview={canReview}
              onReply={() => {
                setReplyMode('reply')
                setReplyOpen(true)
              }}
              onSelfAssign={selfAssign}
              onTransfer={() => setDrawer('transfer')}
              onReview={() => setReviewOpen(true)}
              onSendForApproval={() => setSendApprovalOpen(true)}
              onClosePanel={onClose}
            />

            {/* A merged secondary still shows the conversation that happened on
                it, read-only. Say where the ticket continues and offer the way
                there, so nobody replies on the closed copy. */}
            {conversation.mergedIntoId && (
              <div className="flex items-center gap-2 border-b border-violet-100 bg-violet-50/60 px-4 py-2 text-xs text-violet-900">
                <GitMerge className="h-3.5 w-3.5 shrink-0 text-violet-500" />
                <span>
                  This conversation was merged into{' '}
                  <button
                    type="button"
                    onClick={() => setNavId(conversation.mergedIntoId!)}
                    className="font-semibold underline underline-offset-2 hover:text-violet-700"
                  >
                    #{conversation.mergedIntoReferenceId ?? 'the primary conversation'}
                  </button>
                  . This thread is read-only — reply there to continue it.
                </span>
              </div>
            )}

            {/* Reference-style info strip */}
            <DetailStrip
              conversation={conversation}
              activityLog={activityLog}
              // Status is the assignee's call — an unassigned conversation is nobody's
              // to move, so the dropdown stays locked until it's picked up.
              canChangeStatus={canEdit}
              onReopen={() => void actions.reopen()}
              onHold={() => void actions.onHold()}
              onOpenCloseDrawer={() => setDrawer('close')}
              onSelectStatus={(statusId) => void actions.updateProps({ conversationStatusId: statusId })}
            />

            {/* Two-column body */}
            <div className="flex min-h-0 flex-1">
              {/* Left: subject + description + attachments + thread / tabs */}
              <div className="flex min-w-0 flex-1 flex-col border-r border-slate-200 bg-white">
                {(conversation.subject || conversation.description) && (
                  <div className="shrink-0 border-b border-slate-200">
                    {conversation.subject && (
                      <div className="bg-slate-50 px-5 py-2.5">
                        <span className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                          Subject
                        </span>
                        <span className="ml-2 text-sm font-medium text-slate-800">{conversation.subject}</span>
                      </div>
                    )}
                    {conversation.description &&
                      (conversation.channelOrigin ?? '').toLowerCase() !== 'whatsapp' &&
                      (conversation.channelOrigin ?? '').toLowerCase() !== 'instagram' && (
                        <div className="max-h-72 overflow-y-auto">
                          <DescriptionTranscript
                            description={htmlToText(conversation.description)}
                            channelOrigin={conversation.channelOrigin}
                            customerName={
                              formatCustomerName(
                                conversation.displayName,
                                conversation.primaryHandle,
                                conversation.channelOrigin,
                                conversation.fbAccountHolderName,
                              )
                            }
                          />
                        </div>
                      )}
                  </div>
                )}
                {/* WhatsApp Chat Banner */}
                {(conversation.channelOrigin ?? '').toLowerCase() === 'whatsapp' && (
                  <WhatsAppChatBanner
                    accountHolderName={
                      formatCustomerName(
                        conversation.displayName,
                        conversation.primaryHandle,
                        conversation.channelOrigin,
                      )
                    }
                    accountId={cleanIdentifier(conversation.primaryHandle)}
                    description={conversation.description ? htmlToText(conversation.description) : null}
                    messages={messages}
                  />
                )}
                {/* Instagram Post or Chat Banner */}
                {(conversation.channelOrigin ?? '').toLowerCase() === 'instagram' &&
                  (Boolean(instagramPost.postId || instagramPost.postMessage || instagramPost.postPicture || instagramPost.permalink) ? (
                    <InstagramPostBanner
                      postId={instagramPost.postId}
                      postMessage={instagramPost.postMessage}
                      postPicture={instagramPost.postPicture}
                      permalink={instagramPost.permalink}
                      accountHolderName={
                        conversation.igAccountHolderName ||
                        formatCustomerName(
                          conversation.displayName,
                          conversation.primaryHandle,
                          conversation.channelOrigin,
                        )
                      }
                      accountId={instagramPost.postId || cleanIdentifier(conversation.primaryHandle)}
                    />
                  ) : (
                    <InstagramChatBanner
                      accountHolderName={
                        conversation.igAccountHolderName ||
                        formatCustomerName(
                          conversation.displayName,
                          conversation.primaryHandle,
                          conversation.channelOrigin,
                        )
                      }
                      accountId={cleanIdentifier(conversation.primaryHandle)}
                      description={conversation.description ? htmlToText(conversation.description) : null}
                      messages={messages}
                    />
                  ))}
                {/* Which Facebook post this comment — and therefore this ticket —
                    belongs to. A comment carries none of the post's content, so
                    without this the agent cannot tell what was commented on.
                    Only render for Facebook channel conversations that have post details. */}
                {(conversation.channelOrigin ?? '').toLowerCase() === 'facebook' &&
                  Boolean(facebookPost.postId || facebookPost.postMessage || facebookPost.postPicture || facebookPost.permalink) && (
                    <FacebookPostBanner
                      postId={facebookPost.postId}
                      postMessage={facebookPost.postMessage}
                      postPicture={facebookPost.postPicture}
                      permalink={facebookPost.permalink}
                      accountHolderName={
                        conversation.fbAccountHolderName ||
                        formatCustomerName(
                          conversation.displayName,
                          conversation.primaryHandle,
                          conversation.channelOrigin,
                        )
                      }
                      accountId={facebookPost.postId || cleanIdentifier(conversation.primaryHandle)}
                    />
                  )}
                <AttachmentsSection
                  messages={messages}
                  conversationAttachments={conversationAttachments}
                  onSelect={(i) => { setViewerIndex(i); setViewerOpen(true) }}
                />
                <ConversationTabs
                  conversation={conversation}
                  messages={messages}
                  activityLog={activityLog}
                  currentAgentId={currentAgentId}
                  canEdit={canEdit}
                  canReply={canReply}
                  appendMessage={appendMessage}
                  replaceMessage={replaceMessage}
                  markMessageFailed={markMessageFailed}
                  ratingScale={ratingScale}
                  tab={tab}
                  onTabChange={setTab}
                  onAddNote={() => {
                    setReplyMode('note')
                    setReplyOpen(true)
                  }}
                  onCreateChild={() => setChildChooserOpen(true)}
                  canAddChild={!addChildDisabled}
                  addChildDisabledReason={railReason('child')}
                  onLinkConversation={() => setLinkOpen(true)}
                  onNavigateToConversation={(id) => setNavId(id)}
                  onMergeTickets={(picked) => {
                    setMergeCandidates([
                      {
                        id: conversation.id,
                        referenceId: conversation.referenceId,
                        subject: conversation.subject ?? null,
                        status: conversation.status ?? null,
                        requester: conversation.displayName ?? conversation.primaryHandle ?? null,
                        // The open conversation's own eligibility, so the dialog
                        // can rule it out as the primary before submitting. The
                        // detail response carries parentId/isParent rather than
                        // a reference or child count, hence the boolean forms.
                        hasParent: Boolean(conversation.parentId),
                        hasChildren: conversation.isParent,
                        mergedIntoReferenceId: conversation.mergedIntoReferenceId ?? null,
                        secondaryCount: conversation.mergedSecondaryCount ?? 0,
                        linkedRelationCount: conversation.linkedCount ?? 0,
                        approvalStatus: conversation.approvalStatus ?? null,
                      },
                      ...picked,
                    ])
                  }}
                  onRemoveCoAgent={actions.removeCoAgent}
                  busy={actions.busy}
                  linkedRefreshKey={linkedRefreshKey}
                  transfersRefreshKey={transfersRefreshKey}
                  onChanged={refetchAll}
                  onAttachmentSelect={(attId) => {
                    const idx = allAttachments.findIndex((a) => a.id === attId)
                    if (idx >= 0) {
                      setViewerIndex(idx)
                      setViewerOpen(true)
                    } else {
                      // Attachment not in allAttachments (e.g. note attachment)
                      // — fetch notes to find it, then open viewer with merged list
                      void api.get<{ notes: Array<{ attachments: Array<{ id: string; filename: string; mimeType: string; sizeBytes: number; url: string }> }> }>(`/conversations/${conversation.id}/notes`).then((res) => {
                        const noteAtts: (typeof allAttachments[number])[] = []
                        const seen = new Set(allAttachments.map((a) => a.id))
                        for (const n of res.notes ?? []) {
                          for (const a of (n.attachments ?? [])) {
                            if (typeof a === 'object' && !seen.has(a.id)) {
                              seen.add(a.id)
                              noteAtts.push({ id: a.id, filename: a.filename, mimeType: a.mimeType, sizeBytes: a.sizeBytes, url: a.url })
                            }
                          }
                        }
                        const merged = [...allAttachments, ...noteAtts]
                        const mergedIdx = merged.findIndex((a) => a.id === attId)
                        if (mergedIdx >= 0) {
                          // Temporarily set viewer attachments by pushing noteAtts into state
                          setExtraAttachments(noteAtts)
                          setViewerIndex(mergedIdx)
                          setViewerOpen(true)
                        }
                      })
                    }
                  }}
                />
              </div>

              {/* Right: relationship actions + properties + SLA (collapsible) */}
              {propertiesOpen ? (
                <aside className="w-[460px] shrink-0 space-y-3 overflow-y-auto bg-[#F8FAFC] p-3">
                  <div className="flex items-center gap-1 rounded-[3px] border border-slate-200 bg-white p-1">
                    <RailAction icon={Network} label="Add Child" onClick={() => setChildChooserOpen(true)} disabled={addChildDisabled} title={railReason('child')} />
                    <RailAction icon={Link2} label="Link" onClick={() => setLinkOpen(true)} disabled={linkDisabled} title={railReason('link')} />
                    <RailAction icon={UserPlus} label="Collaborate" onClick={() => setDrawer('collaborate')} disabled={collaborateDisabled} title={railReason('collab')} />
                    <button
                      type="button"
                      onClick={togglePropertiesRail}
                      title="Hide properties panel"
                      className={`flex shrink-0 items-center justify-center ${R} p-2 text-slate-400 transition-colors hover:bg-slate-50 hover:text-slate-600`}
                    >
                      <ChevronsRight className="h-4 w-4" />
                    </button>
                  </div>
                  <PropertiesPanel
                    conversation={conversation}
                    onPatch={actions.updateProps}
                    canEdit={canEdit}
                    onSelfAssign={selfAssign}
                    ratingScale={ratingScale}
                  />
                  <SlaOlaPanel conversation={conversation} />
                </aside>
              ) : (
                <aside className="flex w-11 shrink-0 flex-col items-center gap-2 bg-[#F8FAFC] p-1.5">
                  <button
                    type="button"
                    onClick={togglePropertiesRail}
                    title="Show properties panel"
                    className={`flex items-center justify-center ${R} border border-slate-200 bg-white p-2 text-[#3366FF] shadow-sm transition-colors hover:bg-blue-50`}
                  >
                    <ChevronsLeft className="h-4 w-4" />
                  </button>
                  <span className="select-none text-[10px] font-bold uppercase tracking-widest text-slate-400 [writing-mode:vertical-rl]">
                    Properties
                  </span>
                </aside>
              )}
            </div>

            {/* Action drawers */}
            <SelfAssignDrawer
              open={drawer === 'self-assign'}
              onClose={() => setDrawer(null)}
              agentId={currentAgentId}
              onAssign={async (patch) => {
                const ok = await actions.updateProps({
                  assignedAgentId: patch.assignedAgentId,
                  campaignId: patch.campaignId,
                  assignedTeamId: patch.assignedTeamId,
                })
                return ok
              }}
            />
            <TransferDrawer open={drawer === 'transfer'} onClose={() => setDrawer(null)} actions={actions} currentAgentId={currentAgentId} />
            <CloseDrawer
              open={drawer === 'close'}
              onClose={() => setDrawer(null)}
              actions={actions}
              conversation={conversation}
            />
            <CollaborateDrawer
              open={drawer === 'collaborate'}
              onClose={() => setDrawer(null)}
              actions={actions}
              conversation={conversation}
            />
            {/* Link flow — reuse the conversation search drawer (multi-select). */}
            {linkOpen && (
              <ConversationSearchDrawer
                open
                onClose={() => setLinkOpen(false)}
                title="Link Conversations"
                subtitle={`Link one or more conversations to #${conversation.referenceId}`}
                info={{
                  lead: `Marks other conversations as related to #${conversation.referenceId} — a cross-reference, not a hierarchy.`,
                  points: [
                    { term: 'What a link does', detail: 'Each linked conversation appears on the Linked tab of the other. Nothing is moved, merged or closed.' },
                    { term: 'Independent', detail: 'Linked conversations keep their own owner, status and SLA clock.' },
                    { term: 'Searching', detail: 'Filter first, then tick every conversation you want linked — the whole selection is linked at once.' },
                    { term: 'Merging', detail: 'A linked conversation cannot take part in a merge. Remove the link on the Linked tab first.' },
                  ],
                }}
                excludeIds={[conversation.id]}
                confirmLabel="Link"
                onConfirm={async (ids) => {
                  let allOk = true
                  for (const cid of ids) {
                    const ok = await actions.link(cid)
                    if (!ok) allOk = false
                  }
                  if (allOk) {
                    setLinkedRefreshKey((k) => k + 1)
                    setTab('linked')
                  }
                  return allOk
                }}
              />
            )}

            {/* Merge — choose the primary, CC and note, then confirm.
                Candidates are selected from the Client Info tab. */}
            {mergeCandidates && (
              <MergeDrawer
                open
                onClose={() => setMergeCandidates(null)}
                candidates={mergeCandidates}
                defaultPrimaryId={conversation.id}
                onConfirm={async (input) => {
                  const result = await actions.mergeInto(input.primaryId, input.secondaryIds, {
                    ...(input.note ? { note: input.note } : {}),
                    noteIsPublic: input.noteIsPublic,
                    addSecondaryRecipientsToCc: input.addSecondaryRecipientsToCc,
                    allowDifferentRequesters: input.allowDifferentRequesters,
                  })
                  if (result.ok) {
                    refetchAll()
                    // Surface the result in the Merged tab (secondaries listed there).
                    setTab('merged')
                  }
                  // A rejection is handed back so the dialog can explain which
                  // conversation the server refused, and why.
                  return result
                }}
              />
            )}
            <ReplyDrawer
              open={replyOpen}
              onClose={() => setReplyOpen(false)}
              conversation={conversation}
              currentAgentId={currentAgentId}
              initialMode={replyMode}
              canEdit={canEdit}
              onPatch={actions.updateProps}
              appendMessage={appendMessage}
              replaceMessage={replaceMessage}
              markMessageFailed={markMessageFailed}
            />
            <ChildTicketDrawer
              open={childChooserOpen}
              onClose={() => setChildChooserOpen(false)}
              parentReferenceId={conversation.referenceId}
              onCreateNew={() => {
                setChildChooserOpen(false)
                setCreateChildOpen(true)
              }}
              onLinkExisting={() => {
                setChildChooserOpen(false)
                setLinkExistingOpen(true)
              }}
            />
            {/* Link-existing search — reusable conversation picker drawer. */}
            {linkExistingOpen && (
              <ConversationSearchDrawer
                open
                onClose={() => setLinkExistingOpen(false)}
                title="Add Existing Conversation"
                subtitle={`Add a child to #${conversation.referenceId}`}
                info={{
                  lead: `Makes conversations that already exist children of #${conversation.referenceId}.`,
                  points: [
                    { term: 'What changes', detail: 'Only the parent link. Each conversation keeps its reference, owner, status and SLA clock.' },
                    { term: 'Only one parent', detail: 'A conversation that is already a child of another cannot be added — detach it there first.' },
                    { term: 'Closing', detail: `#${conversation.referenceId} cannot be closed until every child under it is closed.` },
                    { term: 'Merging', detail: 'Neither side of a parent/child pair can take part in a merge while the relationship exists.' },
                  ],
                }}
                excludeIds={[conversation.id]}
                // Only tickets that can become a child: not closed, no parent yet.
                baseParams={{ excludeClosed: '1', withoutParent: '1' }}
                confirmLabel="Add as Child"
                onConfirm={async (ids) => {
                  let allOk = true
                  for (const id of ids) {
                    const ok = await actions.addChild(id)
                    if (!ok) allOk = false
                  }
                  if (allOk) setTab('parentchild')
                  return allOk
                }}
              />
            )}
            {/* Reuse the full New Conversation form to create a child conversation. */}
            <CreateConversationPanel
              open={createChildOpen}
              onClose={() => setCreateChildOpen(false)}
              onCreate={() => {}}
              parent={{
                conversationId: conversation.id,
                referenceId: conversation.referenceId,
                subject: conversation.subject,
                description: conversation.description,
                clientId: conversation.clientId,
                clientName: conversation.displayName,
                clientEmail: conversation.primaryHandle,
                campaignId: conversation.campaignId,
                teamId: conversation.assignedTeamId,
                priorityId: conversation.priorityId,
                tags: conversation.tags,
                // Fixed classification properties
                typeId: conversation.typeId,
                categoryId: conversation.categoryId,
                subcategoryId: conversation.subcategoryId,
                statusId: conversation.statusId,
                impactId: conversation.impactId,
                urgencyId: conversation.urgencyId,
                complexityId: conversation.complexityId,
                locationId: conversation.locationId,
                branchId: conversation.branchId,
                departmentId: conversation.departmentId,
                // Additional properties
                additionalProperties: conversation.additionalProperties,
              }}
              onChildCreated={() => {
                setCreateChildOpen(false)
                refetchAll()
                setTab('parentchild')
                toast.success('Child conversation created')
              }}
            />
            <ApprovalsDrawer
              open={approvalsOpen}
              onClose={() => setApprovalsOpen(false)}
              pendingApprovals={pendingApprovals}
              messages={messages}
              onDecided={refetchAll}
            />
            <SendForApprovalDrawer
              open={sendApprovalOpen}
              onClose={() => setSendApprovalOpen(false)}
              conversationId={conversation.id}
              onSent={refetchAll}
            />
            <ReviewDrawer
              open={reviewOpen}
              onClose={() => setReviewOpen(false)}
              conversationId={conversation.id}
              currentAgentId={currentAgentId}
              onReviewed={refetchAll}
            />
          </>
        )}
      </div>

           {/* Attachment viewer slide-in */}
      <AttachmentViewer
        attachments={viewerAttachments}
        index={viewerIndex}
        open={viewerOpen}
        onClose={() => setViewerOpen(false)}
        onNavigate={setViewerIndex}
      />
    </>
  )
}

/* ── Right-rail relationship action button (Add Child / Link / Collaborate) ── */
function RailAction({
  icon: Icon,
  label,
  onClick,
  disabled = false,
  title,
}: {
  icon: LucideIcon
  label: string
  onClick: () => void
  disabled?: boolean
  title?: string | undefined
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={`flex flex-1 items-center justify-center gap-1.5 ${R} px-2 py-2 text-xs font-semibold transition-colors ${
        disabled ? 'cursor-not-allowed text-slate-300' : 'text-slate-600 hover:bg-slate-50'
      }`}
    >
      <Icon className={`h-4 w-4 ${disabled ? 'text-slate-300' : 'text-[#3366FF]'}`} />
      {label}
    </button>
  )
}

/* ── Loading / error states ── */
function PanelSkeleton({ onClose }: { onClose: () => void }): JSX.Element {
  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center justify-between border-b border-slate-200 bg-white px-5 py-4">
        <div className={`h-6 w-48 animate-pulse ${R} bg-slate-100`} />
        <button onClick={onClose} className="text-sm text-slate-400 hover:text-slate-600">
          Close
        </button>
      </div>
      <div className="flex-1 space-y-3 p-5">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className={`h-16 animate-pulse ${R} bg-slate-100`} />
        ))}
      </div>
    </div>
  )
}

function PanelError({
  message,
  onRetry,
  onClose,
}: {
  message: string
  onRetry: () => void
  onClose: () => void
}): JSX.Element {
  return (
    <div className="flex h-full flex-col items-center justify-center gap-3 p-8 text-center">
      <p className="text-sm text-slate-600">{message}</p>
      <div className="flex gap-2">
        <button onClick={onRetry} className={`${R} bg-[#3366FF] px-4 py-2 text-sm font-semibold text-white hover:bg-[#2952CC]`}>
          Retry
        </button>
        <button onClick={onClose} className={`${R} px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-100`}>
          Close
        </button>
      </div>
    </div>
  )
}
